import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  changePassword,
  clearChangePasswordError,
} from "../store/slices/usersSlice";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { User } from "types/users";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  const { changePasswordLoading, changePasswordError, changePasswordSuccess } =
    useSelector((state: RootState) => state.users);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = () => {
    const updatedPassword = {
      old_password: currentPassword,
      new_password: newPassword,
    };
    dispatch(changePassword(updatedPassword));
  };

  const handleClose = () => {
    dispatch(clearChangePasswordError());
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    onClose();
  };

  useEffect(() => {
    if (changePasswordSuccess) {
      onClose();
    }
  }, [changePasswordSuccess, onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          minWidth: 600, // 👈 Increased width
          minHeight: 400, // 👈 Optional: to increase height too
        }}
      >
        {changePasswordError && (
          <Box display="flex" alignItems="center" color="error.main" mb={2}>
            <ErrorOutlineIcon sx={{ mr: 1 }} />
            <Typography color="error">{changePasswordError}</Typography>
          </Box>
        )}

        <Typography variant="h6" mb={2}>
          Change Password
        </Typography>

        <Typography mb={3}>
          Please enter your current and new password below.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2} mb={3}>
          <TextField
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            disabled={changePasswordLoading}
            startIcon={
              changePasswordLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            Change Password
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
